import { observable, action, makeObservable, runInAction } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import i18next from "i18next";
import {
    GetAgendaitemsToCopy,
    GetAgendaitemsToCopy_agendaitemtexts_by_assembly_agendaitemtexts,
    GetAgendaitemsToCopyVariables
} from "src/api/generated/GetAgendaitemsToCopy";
import {
    DELETE_ASSEMBLY_PROTOCOL_FILE_MANAGER,
    GET_AGENDAITEMS_TEMPLATE,
    GET_AGENDAITEMS_TO_COPY,
    INSERT_ASSEMBLY_AGENDAITEMS
} from "src/api/assembly";
import { GetAgendaitemInformationFromView_agendaitemtemplatetexts } from "src/api/generated/GetAgendaitemInformationFromView";
import { GetAgendaitemTemplate } from "src/api/generated/GetAgendaitemTemplate";
import {
    GetAgendaitemsByAssemblyId,
    GetAgendaitemsByAssemblyId_agendaitem_parents,
    GetAgendaitemsByAssemblyIdVariables
} from "src/api/generated/GetAgendaitemsByAssemblyId";
import {
    DELETE_ALL_AGENDAITEMS_BY_ASSEMBLYID,
    GET_AGENDAITEMS_BY_ASSEMBLYID,
    UPSERT_AGENDAITEM_PROTOCOLTEXT
} from "src/api/agenda";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey, Role } from "src/network/User";
import { FILE_ENTITY, FILE_TYPE } from "src/enums";
import { IFile } from "src/types";
import { MessageType } from "src/components/notifications/Notifier";
import { format } from "date-fns-tz";
import { de, fr, enGB, it } from "date-fns/locale";
import { Route, selectRoute } from "src/config/routes";
import { RealestateCondominium } from "src/stores/RealestateStore";
import { v4 as uuidv4 } from "uuid";
import { AgendaItem, AgendaItemChild } from "src/types";
import {
    InsertAssemblyAgendaitems,
    InsertAssemblyAgendaitemsVariables
} from "src/api/generated/InsertAssemblyAgendaitems";
import { ASSEMBLY_STATE, LOADING_TYPE } from "./AssemblyStore";
import {
    DeleteAssemblyProtocolFileManager,
    DeleteAssemblyProtocolFileManagerVariables
} from "src/api/generated/DeleteAssemblyProtocolFileManager";
import Axios from "src/network/Axios";
import {
    UpsertAgendaItemProtocoltext,
    UpsertAgendaItemProtocoltextVariables
} from "src/api/generated/UpsertAgendaItemProtocoltext";
import { getDate } from "src/utils/Date";
import {
    DeleteAllAgendaItemsByAssemblyId,
    DeleteAllAgendaItemsByAssemblyIdVariables
} from "src/api/generated/DeleteAllAgendaItemsByAssemblyId";
import { getHtmlStringForPreview } from "src/utils/HTMLUtils";
import { ensurePdfExtension, sanitizeAssemblyTitleForFileName } from "src/utils/Common";

export enum AGENDAITEM_PROPERTY {
    TEXT = "text",
    DESCRIPTION = "description",
    INTERNALCOMMENTS = "internalcomments",
    PROTOCOLTEXT = "protocoltext"
}

export enum AGENDAITEM_TABS {
    AGENDAITEMLIST = "agendaitemlist",
    ALLDOCUMENTS = "allDocuments"
}

export enum ProtocolTagKeys {
    Accepted = "screens.assemblies.agenda.tags.accepted",
    Rejected = "screens.assemblies.agenda.tags.rejected",
    ValueQuota = "screens.assemblies.agenda.tags.valueQuota",
    Owner = "screens.assemblies.agenda.tags.owner",
    Decision = "screens.assemblies.agenda.tags.decision"
}

export class AgendaItemStore {
    rootStore: RootStore;

    currentAgendaItemToCopy: GetAgendaitemsToCopy_agendaitemtexts_by_assembly_agendaitemtexts[] = [];
    agendaItemTemplates: GetAgendaitemInformationFromView_agendaitemtemplatetexts[] = [];
    agendaItemTemplatesCount: number = 0;
    currentAgendaItems: AgendaItem[] = [];
    currentAssemblyId: string = "";
    selectedAssembly: string = "";
    selectedRealestate: RealestateCondominium | undefined;
    isDropdownOpen: boolean = false;
    currentAgendaItem?: AgendaItem | AgendaItemChild;
    insertAgendaitems: any;
    deletedAgendaItemIds: string[] = [];
    agendaItemsHaveChanged: boolean = false;
    originalAgendaItems: AgendaItem[] = [];
    assemblyPDFId: string = "";
    isModalUnlockDisplayed: boolean = false;
    isAgendaItemsSidebarEditing: boolean = false;
    isDownloadAgendaItemFiles: boolean = false;
    viewVersion: number = 0;
    isResetAgendaItemsModalDisplayed: boolean = false;
    appendToProtocolText?: (text: string) => void;
    selectedExternalAgendaFile: File | undefined = undefined;
    isUploadingExternalAgendaFile: boolean = false;

    error?: {
        title: string;
    } = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            currentAgendaItemToCopy: observable,
            agendaItemTemplates: observable,
            agendaItemTemplatesCount: observable,
            currentAgendaItems: observable,
            currentAssemblyId: observable,
            selectedAssembly: observable,
            selectedRealestate: observable,
            isDropdownOpen: observable,
            currentAgendaItem: observable,
            insertAgendaitems: observable,
            deletedAgendaItemIds: observable,
            agendaItemsHaveChanged: observable,
            originalAgendaItems: observable,
            assemblyPDFId: observable,
            isModalUnlockDisplayed: observable,
            isAgendaItemsSidebarEditing: observable,
            isDownloadAgendaItemFiles: observable,
            viewVersion: observable,
            isResetAgendaItemsModalDisplayed: observable,

            loadAgendaItemsToCopy: action,
            loadAgendaItemTemplates: action,
            setCurrentAssemblyId: action,
            downloadAgendaItemPDF: action,
            getDefaultFileName: action,
            replaceAssemblyTitle: action,
            updateNavigation: action,
            loadAndCheckAssembly: action,
            toggleDropdown: action,
            loadAgendaItemsToCopyFromAssembly: action,
            setSelectedAssembly: action,
            setSelectedRealestate: action,
            loadRealestateAgendaItems: action,
            setCurrentAgendaItems: action,
            updateSortAgendaItemList: action,
            removeItemById: action,
            handleDragEnd: action,
            handleAddNewItem: action,
            setCurrentAgendaItem: action,
            saveAgendaItems: action,
            saveAgendaItemsAndHeaderFooter: action,
            createInsertAgendaitems: action,
            addDeletedAgendaItemId: action,
            setAgendaItemsHaveChanged: action,
            setOriginalAgendaItems: action,
            updateAgendaItem: action,
            mapAgendaitemParent: action,
            removeAgendaItemFile: action,
            setAssemblyPDFId: action,
            setIsModalUnlockDisplayed: action,
            deleteAssemblyProtocolFiles: action,
            navigateBack: action,
            navigateForward: action,
            saveProtocolText: action,
            setIsAgendaItemsSidebarEditing: action,
            setIsDownloadAgendaItemFiles: action,
            downloadAgendaItemFiles: action,
            deleteAllAgendaItems: action,
            setIsResetAgendaItemsModalDisplayed: action,
            setSelectedExternalAgendaFile: action,
            uploadExternalAgendaFile: action,

            selectedExternalAgendaFile: observable,
            isUploadingExternalAgendaFile: observable
        });
    }

    getDateString = (date: any, formatStr = "PP"): string => {
        let locale: Locale = de;
        if (i18next.language.includes("de")) locale = de;
        if (i18next.language.includes("en")) locale = enGB;
        if (i18next.language.includes("fr")) locale = fr;
        if (i18next.language.includes("it")) locale = it;

        return format(date, formatStr, { locale });
    };

    updateNavigation(title: string | null, middleButton: any = null, rightButton: any = null, backButton: any = null) {
        const { navStore } = this.rootStore;
        runInAction(() => {
            navStore.setTitle(title || "");
            navStore.setActionButtonMiddle(middleButton);
            navStore.setActionButtonRight(rightButton);
            navStore.setBackbutton(backButton);
        });
    }

    loadRealestateAgendaItems(realestateId: string, assemblyId: string, date: string) {
        this.setSelectedRealestate(
            this.rootStore.assemblyStore.formattedRealestates.find((r) => r.id === realestateId)
        );
        this.loadAgendaItemsToCopy(realestateId, assemblyId, date);
        runInAction(() => {
            this.setSelectedAssembly(
                this.currentAgendaItemToCopy.length > 0 ? this.currentAgendaItemToCopy[0].assemblyid : ""
            );
        });
    }

    loadAndCheckAssembly(
        assemblyId: string,
        redirectCallback: (route: string) => void,
        warningCallback: (message: string) => void
    ) {
        return this.rootStore.assemblyStore.loadCurrentAssembly(assemblyId).then(() => {
            if (this.rootStore.assemblyStore.currentAssemblyRealestates.length === 0) {
                const route = selectRoute(Route.assemblyDetails, this.rootStore.authStore.user?.role, {
                    assemblyid: assemblyId
                });
                warningCallback(i18next.t("screens.assemblies.realestate_required"));
                redirectCallback(route);
            }
        });
    }

    toggleDropdown(state?: boolean) {
        this.isDropdownOpen = state !== undefined ? state : !this.isDropdownOpen;
    }

    setAssemblyPDFId(assemblyPDFId: string) {
        this.assemblyPDFId = assemblyPDFId;
    }

    async loadAgendaItemsToCopyFromAssembly(assemblyId: string) {
        try {
            // Ensure current assembly is set
            const currentAssembly = this.rootStore.assemblyStore.currentAssembly;
            if (!currentAssembly) {
                throw new Error("Assembly is not loaded");
            }

            // Determine real estate to load agenda items for
            const realestates = this.rootStore.assemblyStore.formattedRealestates;
            const realestate = realestates?.[0];
            if (!realestate) {
                console.warn("No real estate found for this assembly.");
                return;
            }

            // Set selected real estate in store (if needed)
            this.rootStore.assemblyStore.setSelectedCopyAssemblyAgendaOption(realestates.length > 0 ? 1 : 2);

            // Load agenda items
            const date = currentAssembly.date || "";
            await this.loadAgendaItemsToCopy(realestate.id, assemblyId, date);

            // Update state for selected assembly
            runInAction(() => {
                this.setSelectedAssembly(
                    this.currentAgendaItemToCopy && this.currentAgendaItemToCopy.length > 0
                        ? this.currentAgendaItemToCopy[0].assemblyid
                        : ""
                );
            });
        } catch (error) {
            console.error("Failed to load agenda items to copy:", error);
        }
    }

    loadAgendaItemsToCopy = async (realestateId: string, assemblyId: string, assemblydate: string) => {
        try {
            const { data }: { data: GetAgendaitemsToCopy } = await apolloClientInstance.query<
                GetAgendaitemsToCopy,
                GetAgendaitemsToCopyVariables
            >({
                query: GET_AGENDAITEMS_TO_COPY,
                fetchPolicy: "no-cache",
                variables: {
                    assemblyid: assemblyId,
                    realestateid: realestateId,
                    assemblydate: assemblydate
                }
            });

            runInAction(() => {
                this.currentAgendaItemToCopy = data.agendaitemtexts_by_assembly[0]?.agendaitemtexts;
            });
        } catch (error) {
            console.error("Error loading agendaitems:", error);
        }
    };

    loadAgendaItemTemplates = async () => {
        try {
            const { data }: { data: GetAgendaitemTemplate } = await apolloClientInstance.query<GetAgendaitemTemplate>({
                query: GET_AGENDAITEMS_TEMPLATE,
                variables: { language: i18next.language }
            });

            runInAction(() => {
                this.agendaItemTemplates = data.agendaitemtemplatetexts;
                this.agendaItemTemplatesCount = data.agendaitemtemplatesCounter?.aggregate?.count || 0;
            });
        } catch (error) {
            console.error("Failed to load agenda item templates:", error);
            throw error;
        }
    };

    getDefaultFileName = (date: string, withInternComments: boolean): string => {
        const defaultName = withInternComments
            ? "screens.realestate.assembly.default_assembly_with_intern_comments_pdf_name"
            : "screens.realestate.assembly.default_assembly_pdf_name";
        return i18next.t(defaultName, {
            date: date?.toString().replaceAll(".", "_")
        });
    };

    replaceAssemblyTitle = (fileName: string, assemblyTitle: string | undefined): string => {
        return fileName.replace(
            "assemblytitle",
            assemblyTitle ? assemblyTitle.replaceAll(" ", "_") : i18next.t("screens.realestate.assemblies.title")
        );
    };

    mapAgendaitemParent = (agendaItemParent: GetAgendaitemsByAssemblyId_agendaitem_parents[]) => {
        return agendaItemParent.map((parent) => ({
            id: parent.id,
            sort: parent.sort,
            text: parent.agendaitems_mls[0]?.text || "",
            description: parent.agendaitems_mls[0]?.description || "",
            internalcomments: parent.agendaitems_mls[0]?.internalcomments || "",
            protocoltext: parent.agendaitems_mls[0]?.protocoltext || "",
            agendaitemfiles: parent.agendaitemfiles,
            hasVotings:
                parent.votingagendaitemsCounter &&
                parent.votingagendaitemsCounter.aggregate?.count &&
                parent.votingagendaitemsCounter.aggregate?.count > 0,
            agendaitemchild:
                parent.agendaitem_children?.map((child) => ({
                    id: child.id,
                    sort: child.sort,
                    text: child.agendaitems_mls[0]?.text || "",
                    description: child.agendaitems_mls[0]?.description || "",
                    internalcomments: child.agendaitems_mls[0]?.internalcomments || "",
                    protocoltext: child.agendaitems_mls[0]?.protocoltext || "",
                    agendaitemfiles: child.agendaitemfiles,
                    hasVotings:
                        child.votingagendaitemsCounter &&
                        child.votingagendaitemsCounter.aggregate?.count &&
                        child.votingagendaitemsCounter.aggregate?.count > 0
                })) || []
        }));
    };

    /**
     * @param resetSelection true when (re)opening the agenda of an assembly: drops state left over from a
     * previously opened assembly (focused item, original items) and selects the first agenda item.
     */
    loadAgendaItems = async (resetSelection: boolean = false) => {
        if (resetSelection) {
            runInAction(() => {
                this.rootStore.assemblyStore.setFocusedAgendaItem(undefined);
                this.setCurrentAgendaItem(undefined);
                this.setOriginalAgendaItems([]);
            });
        }
        const requestedAssemblyId = this.currentAssemblyId;
        try {
            const { data }: { data: GetAgendaitemsByAssemblyId } = await apolloClientInstance.query<
                GetAgendaitemsByAssemblyId,
                GetAgendaitemsByAssemblyIdVariables
            >({
                query: GET_AGENDAITEMS_BY_ASSEMBLYID,
                variables: { language: i18next.language, assemblyid: this.currentAssemblyId },
                fetchPolicy: "no-cache"
            });

            // The user switched to another assembly while loading: this response is stale
            if (requestedAssemblyId !== this.currentAssemblyId) return;

            runInAction(() => {
                this.setCurrentAgendaItems(this.mapAgendaitemParent(data.agendaitem_parents));

                if (this.rootStore.assemblyStore.isAttendanceLiveModeTabActive) {
                    this.setCurrentAgendaItem(undefined);
                } else {
                    if (this.rootStore.assemblyStore.focusedAgendaItem) {
                        this.setCurrentAgendaItem(this.rootStore.assemblyStore.focusedAgendaItem);
                    } else {
                        this.setCurrentAgendaItem(this.currentAgendaItems[0]);
                    }
                }

                if (this.originalAgendaItems.length <= 0) {
                    this.setOriginalAgendaItems(this.mapAgendaitemParent(data.agendaitem_parents));
                }
            });
        } catch (error) {
            console.error("Failed to load agenda item templates:", error);
            throw error;
        }
        this.rootStore.assemblyStore.setLoadingType(LOADING_TYPE.NONE);
    };

    printStatusMessage = (message: string, messageType: MessageType) => {
        this.rootStore.uiStore.enqueueSnackbar({
            content: message,
            options: {
                type: messageType,
                autoClose: messageType === MessageType.ERROR ? false : 3000
            }
        });
    };

    downloadAgendaItemPDF = async (option: number) => {
        if (!this.rootStore.authStore.user || !this.currentAssemblyId) {
            this.printStatusMessage(
                i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                MessageType.ERROR
            );
            return;
        }

        const downloadId =
            option === 1 ? this.currentAssemblyId : option === 2 ? this.assemblyPDFId : this.currentAssemblyId;

        try {
            const response = await Axios.getAuthInstance().get(`${NetworkConfig.downloadFileVersion2}/${downloadId}`, {
                urlWithoutParams: NetworkConfig.downloadFileVersion2,
                responseType: "blob",
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user.role)
                }
            });

            const date = this.rootStore.assemblyStore.currentAssembly?.date
                ? this.getDateString(new Date(this.rootStore.assemblyStore.currentAssembly.date), "dd_MM_yyyy")
                : "";
            let fileName = this.getDefaultFileName(date ?? "", option === 2);
            const title = sanitizeAssemblyTitleForFileName(this.rootStore.assemblyStore.currentAssembly?.title);
            fileName = this.replaceAssemblyTitle(fileName, title);
            fileName = ensurePdfExtension(fileName);

            // Start download
            const fileBlob = response.data;
            const objUrl = window.URL.createObjectURL(fileBlob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            this.printStatusMessage(
                i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                MessageType.ERROR
            );
        }
    };

    updateSortAgendaItemList = () => {
        this.currentAgendaItems.forEach((item, index) => {
            item.sort = index + 1;

            if (item.agendaitemchild) {
                item.agendaitemchild.forEach((childItem, childIndex) => {
                    childItem.sort = childIndex + 1;
                });
            }
        });

        this.currentAgendaItems.sort((a, b) => a.sort - b.sort);
    };

    removeItemById(itemId: string) {
        this.addDeletedAgendaItemId(itemId);
        const removeItem = (items: AgendaItem[], id: string): any[] => {
            return items.filter((item) => {
                if (item.id === id) return false;
                if (item.agendaitemchild) {
                    item.agendaitemchild = removeItem(item.agendaitemchild, id);
                }
                return true;
            });
        };

        runInAction(() => {
            this.currentAgendaItems = removeItem(this.currentAgendaItems, itemId);
            this.updateSortAgendaItemList();
            this.setAgendaItemsHaveChanged();
        });
    }

    moveItemWithinArray<T>(array: T[], fromIndex: number, toIndex: number): void {
        const [movedItem] = array.splice(fromIndex, 1);
        array.splice(toIndex, 0, movedItem);
    }

    updateSortOrder(items: AgendaItemChild[]): void {
        items.forEach((item, index) => {
            item.sort = index + 1;
        });
    }

    getParentIndex = (currentItems: AgendaItem[], droppableId: string): number => {
        const parentIndex = currentItems.findIndex((item) => `children-${item.id}` === droppableId);
        if (parentIndex === -1) {
            console.error("Parent index not found for droppableId:", droppableId);
        }
        return parentIndex;
    };

    validateAndGetChildren = (parentItem: AgendaItem, initializeIfMissing = false): AgendaItemChild[] | null => {
        if (!parentItem.agendaitemchild) {
            if (initializeIfMissing) {
                parentItem.agendaitemchild = [];
                return parentItem.agendaitemchild;
            }
            console.error("agendaitemchild is not defined or is not an array for parent:", parentItem);
            return null;
        }
        return Array.from(parentItem.agendaitemchild);
    };

    handleDragEnd(source: any, destination: any) {
        const currentItems = Array.from(this.currentAgendaItems);

        if (source.droppableId === "agenda-list" && destination.droppableId === "agenda-list") {
            this.moveItemWithinArray(currentItems, source.index, destination.index);
            this.updateSortOrder(currentItems);
        } else if (source.droppableId.startsWith("children-") && source.droppableId === destination.droppableId) {
            const parentIndex = this.getParentIndex(currentItems, source.droppableId);
            if (parentIndex === -1) return;

            const parentItem = currentItems[parentIndex];
            const children = this.validateAndGetChildren(parentItem);
            if (!children) return;

            this.moveItemWithinArray(children, source.index, destination.index);
            this.updateSortOrder(children);
            parentItem.agendaitemchild = children;
        } else if (source.droppableId.startsWith("children-") && destination.droppableId.startsWith("children-")) {
            const sourceParentIndex = this.getParentIndex(currentItems, source.droppableId);
            const destParentIndex = this.getParentIndex(currentItems, destination.droppableId);

            if (sourceParentIndex === -1 || destParentIndex === -1) return;

            const sourceParent = currentItems[sourceParentIndex];
            const destParent = currentItems[destParentIndex];

            const sourceChildren = this.validateAndGetChildren(sourceParent);
            const destChildren = this.validateAndGetChildren(destParent, true);

            if (!sourceChildren || !destChildren) return;

            const [movedChild] = sourceChildren.splice(source.index, 1);
            destChildren.splice(destination.index, 0, movedChild);

            this.updateSortOrder(sourceChildren);
            this.updateSortOrder(destChildren);

            sourceParent.agendaitemchild = sourceChildren;
            destParent.agendaitemchild = destChildren;
        }

        this.currentAgendaItems = currentItems;
        this.setAgendaItemsHaveChanged();
    }

    handleAddNewItem(isParentAction: boolean, parentIndex: number | null, childIndex: number | null) {
        const currentItems = Array.from(this.currentAgendaItems);

        if (isParentAction) {
            // Add a new Parent item ("Traktandum")
            const newParentItem: AgendaItem = {
                id: uuidv4(),
                sort: currentItems.length + 1,
                agendaitemchild: [],
                text: "",
                description: "",
                internalcomments: "",
                protocoltext: "",
                isNew: true,
                agendaitemfiles: [],
                hasVotings: false
            };
            currentItems.splice((parentIndex ?? -1) + 1, 0, newParentItem);

            // Reassign sort orders
            currentItems.forEach((item, index) => {
                item.sort = index + 1;
            });
        } else {
            // Add a new Child item ("Untertraktandum")
            const parentItem = currentItems[parentIndex!];
            if (!parentItem) {
                console.error(`Parent item not found at index ${parentIndex}`);
                return;
            }

            if (!parentItem.agendaitemchild) {
                parentItem.agendaitemchild = [];
            }

            const newChildItem: AgendaItemChild = {
                id: uuidv4(),
                sort: (childIndex ?? parentItem.agendaitemchild.length) + 1,
                text: "",
                description: "",
                internalcomments: "",
                protocoltext: "",
                isNew: true,
                agendaitemfiles: [],
                hasVotings: false
            };

            parentItem.agendaitemchild.splice(
                childIndex !== null ? childIndex + 1 : parentItem.agendaitemchild.length,
                0,
                newChildItem
            );

            // Reassign sort orders for child items
            parentItem.agendaitemchild.forEach((child, index) => {
                child.sort = index + 1;
            });
        }

        this.currentAgendaItems = currentItems;
        this.setAgendaItemsHaveChanged();
    }

    createInsertAgendaitems = () => {
        const insertAgendaitems: any = this.currentAgendaItems.map((agendaItem) => {
            const agendaitemtempManager = agendaItem.agendaitemfiles.map((agendaitemfile) => {
                return {
                    id: agendaitemfile.id,
                    fileid: agendaitemfile.fileid,
                    roleid: 30,
                    visible: true
                };
            });
            const agendaitemtempCoowner = agendaItem.agendaitemfiles.map((agendaitemfile) => {
                return {
                    id: agendaitemfile.id,
                    fileid: agendaitemfile.fileid,
                    roleid: 32,
                    visible: true
                };
            });
            const newAgendaItemFilesList = agendaitemtempManager.concat(agendaitemtempCoowner);

            const newAgendaItemChild = agendaItem.agendaitemchild!.map((agendaitemchild) => {
                return {
                    id: agendaitemchild.id,
                    assemblyid: this.currentAssemblyId,
                    sort: agendaitemchild.sort,
                    agendaitems_mls: {
                        data: {
                            language: i18next.language,
                            text: agendaitemchild.text,
                            description: getHtmlStringForPreview(agendaitemchild.description),
                            internalcomments: getHtmlStringForPreview(agendaitemchild.internalcomments),
                            protocoltext: getHtmlStringForPreview(agendaitemchild.protocoltext)
                        },
                        on_conflict: {
                            constraint: "UNIQUE_agendaitem_agendaitem_mls",
                            update_columns: ["text", "language", "description", "internalcomments", "protocoltext"]
                        }
                    },
                    agendaitemfiles: {
                        data: newAgendaItemFilesList,
                        on_conflict: {
                            constraint: "PK_agendaitemfiles",
                            update_columns: []
                        }
                    }
                };
            });

            return {
                id: agendaItem.id,
                assemblyid: this.currentAssemblyId,
                sort: agendaItem.sort,
                agendaitems_mls: {
                    data: {
                        language: i18next.language,
                        text: agendaItem.text,
                        description: getHtmlStringForPreview(agendaItem.description),
                        internalcomments: getHtmlStringForPreview(agendaItem.internalcomments),
                        protocoltext: getHtmlStringForPreview(agendaItem.protocoltext)
                    },
                    on_conflict: {
                        constraint: "UNIQUE_agendaitem_agendaitem_mls",
                        update_columns: ["text", "language", "description", "internalcomments", "protocoltext"]
                    }
                },
                agendaitemfiles: {
                    data: newAgendaItemFilesList,
                    on_conflict: {
                        constraint: "PK_agendaitemfiles",
                        update_columns: []
                    }
                },
                agendaitems: {
                    data: newAgendaItemChild,
                    on_conflict: {
                        constraint: "PK_agendaitems",
                        update_columns: ["sort", "parentagendaitemid"]
                    }
                }
            };
        });

        this.insertAgendaitems = insertAgendaitems;
    };

    saveAgendaItems = async (): Promise<boolean> => {
        this.rootStore.assemblyStore.setLoadingType(LOADING_TYPE.SAVING_ASSEMBLY);

        this.createInsertAgendaitems();

        try {
            await apolloClientInstance.query<InsertAssemblyAgendaitems, InsertAssemblyAgendaitemsVariables>({
                query: INSERT_ASSEMBLY_AGENDAITEMS,
                fetchPolicy: "no-cache",
                variables: {
                    agendaItems: this.insertAgendaitems,
                    agendaids: this.deletedAgendaItemIds
                }
            });

            runInAction(() => {
                this.printStatusMessage(i18next.t("screens.assemblies.agenda.save_agendaitems"), MessageType.SUCCESS);

                this.agendaItemsHaveChanged = false;

                this.rootStore.assemblyStore.setAgendaItemsExists(this.currentAgendaItems.length > 0);
                this.rootStore.assemblyStore.setLoadingType(LOADING_TYPE.SAVING_ASSEMBLY);
            });

            return true;
        } catch (error) {
            this.printStatusMessage(i18next.t("screens.assemblies.agenda.error_save_agendaitems"), MessageType.ERROR);
            console.error("Error saving agendaitems:", error);
            return false;
        } finally {
            this.rootStore.assemblyStore.setLoadingType(LOADING_TYPE.NONE);
        }
    };

    saveAgendaItemsAndHeaderFooter = async () => {
        const [agendaSaved, headerFooterSaved] = await Promise.all([
            this.saveAgendaItems(),
            this.rootStore.assemblyStore.saveAssemblyHeaderFooter()
        ]);

        if (!agendaSaved || !headerFooterSaved) return;

        const { currentAssembly } = this.rootStore.assemblyStore;
        if (
            currentAssembly?.id &&
            currentAssembly.state !== ASSEMBLY_STATE.PREPARING &&
            currentAssembly.state !== ASSEMBLY_STATE.DATE_PUBLISHED
        ) {
            await this.rootStore.assemblyStore.generateAssemblyRealestatePDF(currentAssembly.id, false);
        }
    };

    saveProtocolText = async () => {
        try {
            await apolloClientInstance.mutate<UpsertAgendaItemProtocoltext, UpsertAgendaItemProtocoltextVariables>({
                mutation: UPSERT_AGENDAITEM_PROTOCOLTEXT,
                variables: {
                    agendaitemId: this.currentAgendaItem?.id,
                    protocoltext: getHtmlStringForPreview(this.currentAgendaItem?.protocoltext)
                }
            });
        } catch (error) {
            console.error("Error saving protocoltext: ", error);
        }
    };

    addDeletedAgendaItemId = (deletedAgendaItemIds: string) => {
        this.deletedAgendaItemIds?.push(deletedAgendaItemIds);
    };

    updateAgendaItem = (property: AGENDAITEM_PROPERTY, newValue: string) => {
        if (this.currentAgendaItem) {
            this.currentAgendaItem[property] = newValue;
        }
    };

    removeAgendaItemFile(fileId: string, agendaItemId: string) {
        // find the parent item in currentAgendaItems
        const parent = this.currentAgendaItems.find((item) => item.id === agendaItemId);
        if (parent) {
            // mutate its agendaitemfiles in”place, so MobX observers fire
            const idx = parent.agendaitemfiles.findIndex((f) => f.fileid === fileId);
            if (idx > -1) parent.agendaitemfiles.splice(idx, 1);
            return;
        }
        // if not a top”level, look for it in children
        this.currentAgendaItems.forEach((item) => {
            item.agendaitemchild?.forEach((child) => {
                if (child.id === agendaItemId) {
                    const idx2 = child.agendaitemfiles.findIndex((f) => f.fileid === fileId);
                    if (idx2 > -1) child.agendaitemfiles.splice(idx2, 1);
                }
            });
        });
    }

    deleteAssemblyProtocolFiles = async (assemblyId?: string) => {
        const targetAssemblyId = assemblyId ?? this.currentAssemblyId;
        try {
            if (targetAssemblyId) {
                await apolloClientInstance.mutate<
                    DeleteAssemblyProtocolFileManager,
                    DeleteAssemblyProtocolFileManagerVariables
                >({
                    mutation: DELETE_ASSEMBLY_PROTOCOL_FILE_MANAGER,
                    variables: {
                        assemblyid: targetAssemblyId
                    }
                });
            }
        } catch (error: any) {
            this.printStatusMessage(i18next.t("screens.realestate.assembly.edit.delete_failed"), MessageType.ERROR);
        }
    };

    deleteAllAgendaItems = async () => {
        try {
            if (this.currentAssemblyId) {
                await apolloClientInstance.mutate<
                    DeleteAllAgendaItemsByAssemblyId,
                    DeleteAllAgendaItemsByAssemblyIdVariables
                >({
                    mutation: DELETE_ALL_AGENDAITEMS_BY_ASSEMBLYID,
                    variables: {
                        assemblyId: this.currentAssemblyId
                    }
                });

                runInAction(() => {
                    this.rootStore.assemblyStore.setAgendaItemsExists(false);
                    this.viewVersion += 1;

                    this.printStatusMessage(
                        i18next.t("screens.assemblies.agenda.success_delete_agendaitems"),
                        MessageType.SUCCESS
                    );

                    if (this.rootStore.assemblyStore.currentAssembly?.state === ASSEMBLY_STATE.AGENDA_PUBLISHED) {
                        this.rootStore.assemblyStore.updateAssemblyState(5);
                    }
                });
            }
        } catch (error: any) {
            this.printStatusMessage(
                i18next.t("screens.assemblies.agenda.failed_delete_agendaitems"),
                MessageType.ERROR
            );
        }
    };

    navigateBack() {
        const flatList = this.getFlatAgendaItems();
        if (!this.currentAgendaItem && flatList.length === 0) return;

        // Coming back from "Close" tab
        if (this.rootStore.assemblyStore.isCloseLiveModeTabActive) {
            const lastItem = flatList[flatList.length - 1];
            this.currentAgendaItem = lastItem;
            this.rootStore.assemblyStore.setIsCloseLiveModeTabActive(false);
            return;
        }

        const currentIndex = flatList.findIndex((item) => item.id === this.currentAgendaItem?.id);

        if (currentIndex === 0) {
            // If currentAgendaItem is the first item, go back to attendancelist
            this.setCurrentAgendaItem(undefined);
            this.rootStore.assemblyStore.setIsAttendanceLiveModeTabActive(true);
            return;
        }

        if (currentIndex > 0) {
            this.currentAgendaItem = flatList[currentIndex - 1];
        }
    }

    navigateForward() {
        const flatList = this.getFlatAgendaItems();
        if (flatList.length === 0) return;

        // Case: Attendance tab is active
        if (this.rootStore.assemblyStore.isAttendanceLiveModeTabActive) {
            this.setCurrentAgendaItem(flatList[0]);
            this.rootStore.assemblyStore.setIsAttendanceLiveModeTabActive(false);
            return;
        }

        // Case: Current item is set
        if (!this.currentAgendaItem) return;

        const currentIndex = flatList.findIndex((item) => item.id === this.currentAgendaItem?.id);

        if (currentIndex < flatList.length - 1) {
            this.setCurrentAgendaItem(flatList[currentIndex + 1]);
        } else {
            this.rootStore.assemblyStore.setIsCloseLiveModeTabActive(true);
            this.setCurrentAgendaItem(undefined);
        }
    }

    // Helper function to flatten parent and child agenda items
    private getFlatAgendaItems(): (AgendaItem | AgendaItemChild)[] {
        return this.currentAgendaItems.reduce<(AgendaItem | AgendaItemChild)[]>((acc, item) => {
            acc.push(item);
            if (item.agendaitemchild) {
                acc.push(...item.agendaitemchild);
            }
            return acc;
        }, []);
    }

    async downloadAgendaItemFiles() {
        if (!this.rootStore.authStore.user || !this.rootStore.assemblyStore.currentAssembly?.id) {
            this.printStatusMessage(
                i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                MessageType.ERROR
            );
            return;
        }

        try {
            this.setIsDownloadAgendaItemFiles(true);
            const response = await Axios.getAuthInstance().get(
                `${NetworkConfig.downloadAgendaItemFiles}/${this.rootStore.assemblyStore.currentAssembly?.id}`,
                {
                    urlWithoutParams: NetworkConfig.downloadAgendaItemFiles,
                    responseType: "blob",
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            const contentDisposition = response.headers["content-disposition"] ?? "";

            const fileNameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
            let fileName = fileNameStarMatch ? decodeURIComponent(fileNameStarMatch[1]) : fileNameMatch?.[1];
            const fileBlob = response.data;

            const objUrl = window.URL.createObjectURL(fileBlob);
            const a = document.createElement("a");
            a.href = objUrl;
            const date = getDate(this.rootStore.assemblyStore.currentAssembly?.date ?? "");
            fileName =
                fileName ??
                `${i18next.t("screens.realestate.assembly.default_agenda_file_name", {
                    date: date?.toString().replaceAll(".", "_")
                })}`;
            const fileNameTitle = this.rootStore.assemblyStore.currentAssembly?.title
                .replaceAll(" ", "_")
                .replace(/[,()/.-]/g, "_")
                .slice(0, 50);
            fileName = fileName.replace("assemblytitle", fileNameTitle ?? "");
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            this.printStatusMessage(
                i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                MessageType.ERROR
            );
        }
        this.setIsDownloadAgendaItemFiles(false);
    }

    /* SETTER */

    setCurrentAssemblyId = (assemblyId: string) => {
        if (this.currentAssemblyId !== assemblyId) {
            // Switching assemblies: drop everything that belongs to the previously opened one
            this.rootStore.assemblyStore.setFocusedAgendaItem(undefined);
            this.currentAgendaItem = undefined;
            this.currentAgendaItems = [];
            this.originalAgendaItems = [];
        }
        this.currentAssemblyId = assemblyId;
    };

    setSelectedAssembly(assemblyId: string) {
        this.selectedAssembly = assemblyId;
    }

    setSelectedRealestate(realestate?: RealestateCondominium) {
        this.selectedRealestate = realestate;
    }

    setCurrentAgendaItems(currentAgendaItems: AgendaItem[]) {
        this.currentAgendaItems = currentAgendaItems;
    }

    setCurrentAgendaItem(currentAgendaItem: AgendaItem | AgendaItemChild | undefined) {
        this.currentAgendaItem = currentAgendaItem;
    }

    setOriginalAgendaItems = (agendaItems: AgendaItem[]) => {
        this.originalAgendaItems = agendaItems;
    };

    setAgendaItemsHaveChanged = () => {
        let agendaItemsHaveChanged = false;

        // Check if the lengths of the two arrays are different
        if (this.currentAgendaItems.length !== this.originalAgendaItems.length) {
            agendaItemsHaveChanged = true;
        } else {
            // Iterate through the agenda items to check for changes
            this.currentAgendaItems.forEach((agendaItem, index) => {
                const originalAgendaItem = this.originalAgendaItems[index];

                // Check if top-level properties are different
                if (
                    agendaItem.id !== originalAgendaItem.id ||
                    agendaItem.text !== originalAgendaItem.text ||
                    agendaItem.description !== originalAgendaItem.description ||
                    agendaItem.internalcomments !== originalAgendaItem.internalcomments ||
                    agendaItem.protocoltext !== originalAgendaItem.protocoltext ||
                    agendaItem.agendaitemfiles.length !== originalAgendaItem.agendaitemfiles.length ||
                    agendaItem.agendaitemchild?.length !== originalAgendaItem.agendaitemchild?.length
                ) {
                    agendaItemsHaveChanged = true;
                    return; // Exit the iteration early
                }

                // Check if child item lengths are different
                const currentChildItems = agendaItem.agendaitemchild || [];
                const originalChildItems = originalAgendaItem.agendaitemchild || [];
                if (currentChildItems.length !== originalChildItems.length) {
                    agendaItemsHaveChanged = true;
                    return; // Exit the iteration early
                }

                // Check each child item for differences
                for (let i = 0; i < currentChildItems.length; i++) {
                    if (
                        currentChildItems[i].id !== originalChildItems[i]?.id ||
                        currentChildItems[i].text !== originalChildItems[i]?.text ||
                        currentChildItems[i].description !== originalChildItems[i]?.description ||
                        currentChildItems[i].internalcomments !== originalChildItems[i]?.internalcomments ||
                        currentChildItems[i].protocoltext !== originalChildItems[i]?.protocoltext ||
                        currentChildItems[i].agendaitemfiles.length !== originalChildItems[i].agendaitemfiles.length
                    ) {
                        agendaItemsHaveChanged = true;
                        break; // Exit the loop early
                    }
                }
            });
        }

        this.agendaItemsHaveChanged = agendaItemsHaveChanged;
    };

    setIsModalUnlockDisplayed(isModalUnlockDisplayed: boolean) {
        this.isModalUnlockDisplayed = isModalUnlockDisplayed;
    }

    setIsAgendaItemsSidebarEditing(isAgendaItemsSidebarEditing: boolean) {
        this.isAgendaItemsSidebarEditing = isAgendaItemsSidebarEditing;
    }

    setIsDownloadAgendaItemFiles(isDownloadAgendaItemFiles: boolean) {
        this.isDownloadAgendaItemFiles = isDownloadAgendaItemFiles;
    }

    setIsResetAgendaItemsModalDisplayed = (isResetAgendaItemsModalDisplayed: boolean) => {
        this.isResetAgendaItemsModalDisplayed = isResetAgendaItemsModalDisplayed;
    };

    setSelectedExternalAgendaFile = (file: File | undefined) => {
        this.selectedExternalAgendaFile = file;
    };

    private fileToBase64DataOnly = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
                const result = String(reader.result ?? "");
                const commaIndex = result.indexOf(",");
                resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
            };
            reader.readAsDataURL(file);
        });
    };

    private buildIFileFromBrowserFile = async (file: File, type: number): Promise<IFile> => {
        const base64String = await this.fileToBase64DataOnly(file);
        const extension = (file.name.split(".").pop() ?? "").toLowerCase();
        const filename = file.name.endsWith(`.${extension}`) ? file.name.replace(`.${extension}`, "") : file.name;

        return {
            mimetype: file.type,
            extension,
            name: filename,
            fileDate: new Date().toISOString(),
            base64String,
            type
        } as IFile;
    };

    uploadExternalAgendaFile = async (agendaItemId: string) => {
        if (!this.selectedExternalAgendaFile) return;

        const file = this.selectedExternalAgendaFile;

        this.isUploadingExternalAgendaFile = true;

        try {
            const metadataFile = await this.buildIFileFromBrowserFile(file, FILE_TYPE.EXTERNAL_AGENDAFILES);
            metadataFile.fileBlob = file;

            await this.rootStore.fileStore.uploadFilesBulkWithRoles(
                metadataFile,
                agendaItemId,
                FILE_ENTITY.COND_AGENDAITEMS,
                [Role.MANAGER, Role.COOWNER]
            );

            runInAction(() => {
                this.selectedExternalAgendaFile = undefined;
            });

            this.printStatusMessage(
                i18next.t("screens.realestate.assembly.agendaitem_edit.add_file_success"),
                MessageType.SUCCESS
            );

            await this.loadAgendaItems();

            const findItem = (id: string) => {
                const parent = this.currentAgendaItems.find((item) => item.id === id);
                if (parent) return parent;
                for (const item of this.currentAgendaItems) {
                    const child = item.agendaitemchild?.find((c) => c.id === id);
                    if (child) return child;
                }
                return undefined;
            };

            const refreshedItem = findItem(agendaItemId);
            if (refreshedItem) {
                this.setCurrentAgendaItem(refreshedItem);
            }
        } catch (e) {
            console.error(e);
            const error = e as { message?: string };
            this.printStatusMessage(
                i18next.t("screens.realestate.assembly.agendaitem_edit.add_error") + error.message,
                MessageType.ERROR
            );
        } finally {
            runInAction(() => {
                this.isUploadingExternalAgendaFile = false;
            });
        }
    };
}
